import { Search } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import get from 'lodash-es/get';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CLIENTS_LIST_URL } from '../../constants/apiUrls';
import { CLIENTS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { getRequestJSON } from '../../util/services';
import { headerStyles } from '../Contracts/ContractsTable';

function ClientsList() {
  // Clients index opts into the per-domain flip (ACH-colk). The
  // {collection,pagination} envelope is shape-identical across both backends
  // for the fields this table reads (collection[].id/name/categorization +
  // pagination.total_items/per_page); RD-3 pagination meta is unread here.
  // ACH honors the same `page`/`term` query params (client/inputs.go).
  const { apiHostFor } = useConfig();
  const clientsHost = apiHostFor('clients');
  const { constants } = useConstants();
  const [clientsList, setClientsList] = useState([]);
  const [paginationState, setPagination] = useState({
    total_items: 0,
    per_page: 5,
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [term, setTerm] = useState('');

  const ACCEPTED_CHANNELS = get(constants, 'CONTRACT.ACCEPTED_CHANNELS');
  const ACCEPTED_REACHES = get(constants, 'CONTRACT.ACCEPTED_REACHES');
  const ACCEPTED_SOURCES = get(constants, 'CONTRACT.ACCEPTED_SOURCES');

  useEffect(() => {
    const getItems = async () => {
      const path = generatePath(API_CLIENTS_LIST_URL, {
        '*': `?${queryString.stringify({
          // Server page is 1-index and front-end is 0-index based.
          page: `${page + 1}`,
          term,
        })}`,
      });
      setIsLoading(true);
      const response = await getRequestJSON(clientsHost, path);
      setIsLoading(false);

      if (!response) {
        return;
      }

      const { collection, pagination: paginationInfo } = response;

      const normalizedCollection = collection.map((item) => ({
        ...item,
        // Need to normalize the categorization
        channel: ACCEPTED_CHANNELS && ACCEPTED_CHANNELS[item.categorization[0]],
        reach: ACCEPTED_REACHES && ACCEPTED_REACHES[item.categorization[1]],
        source: ACCEPTED_SOURCES && ACCEPTED_SOURCES[item.categorization[2]],
      }));

      setClientsList(normalizedCollection);
      setPagination(paginationInfo);
    };

    getItems();
  }, [page, clientsHost, ACCEPTED_CHANNELS, ACCEPTED_REACHES, ACCEPTED_SOURCES, term]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const headerStylesCss = headerStyles();

  return (
    <Box p={2}>
      <Box sx={{ display: 'flex', justifyContent: 'end', flexDirection: 'row' }}>
        <Stack spacing={4} direction="row" alignItems="center">
          <TextField
            sx={{ backgroundColor: 'white' }}
            variant="outlined"
            placeholder="Search"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isLoading ? <CircularProgress color="inherit" size={24} /> : <Search />}
                </InputAdornment>
              ),
            }}
            onKeyDown={(event: any) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setTerm(event.target.value);
              }
            }}
            autoComplete="off"
          />
        </Stack>
      </Box>
      <Box mt={2}>
        <TableContainer className={headerStylesCss.root}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{}}>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell align="left">Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Loading Skeleton */}
              {isLoading &&
                [...Array(50)].map((item, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope="row">
                      <Skeleton animation="wave" width={500} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                    <TableCell align="left">
                      <Skeleton animation="wave" width={87} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                  </TableRow>
                ))}
              {clientsList &&
                clientsList.length > 0 &&
                clientsList.map((client) => (
                  <TableRow
                    key={`link-${client.id}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    onClick={() => navigate(generatePath(CLIENTS_EDIT_URL, { id: client.id }))}
                  >
                    <TableCell scope="row">
                      {client.name} ({client.id})
                    </TableCell>
                    <TableCell align="left">
                      Channel: {ACCEPTED_CHANNELS && ACCEPTED_CHANNELS[client.categorization[0]]} | Reach:{' '}
                      {ACCEPTED_REACHES && ACCEPTED_REACHES[client.categorization[1]]} | Source:{' '}
                      {ACCEPTED_SOURCES && ACCEPTED_SOURCES[client.categorization[2]]}
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow key="pagination">
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  count={paginationState.total_items} // served static by backend
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={paginationState.per_page} // served static by backend
                  onRowsPerPageChange={() => {}}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default ClientsList;
